/* jshint esversion: 6 */
import React from 'react';
import ConstantMixin from 'app/mixins/ConstantMixin.js';
import ConnectSocialDialog from '@/shadcn/components/AddSocialModal/ConnectSocialDialog.tsx';
import { initialsFromName } from '@/shadcn/components/NewAutomation/OutputTile.tsx';
import { assetBase } from '@/lib/asset-base.ts';
import { networkFromServiceCode } from '@/lib/network-service-codes.ts';

/*
 * "Reuse <service> input" — the credentials already connected for one social
 * source, offered instead of sending the user through the OAuth hop again.
 * Opened from ChooseFeedType when `automatedRoutes.socialInputCreds[type]` is
 * non-empty.
 *
 * Drawn as the connect dialog's "accounts" screen: the same card, header and
 * tile grid the output picker uses, since this is the same act of choosing an
 * account. There is no footer — picking a tile IS the action, so a Connect
 * button would have nothing left to confirm.
 */

/* The product rule the design carries: below this the whole list is on screen
   already, so a filter is noise. */
const SEARCH_THRESHOLD = 10;

class ReuseSocialInputModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            query: '',
            searchOpen: false
        };

        this.onSelect = this.onSelect.bind(this);
        this.matches = this.matches.bind(this);

    }

    /*
     * Read from props rather than copied into state on mount, which is what this
     * used to do: `socialInputCreds` is fetched, so a list that arrived after
     * mount left the dialog showing the empty one it had captured.
     */
    creds() {
        return (Array.isArray(this.props.credList)) ? this.props.credList : [];
    }

    matches() {
        let query = this.state.query.trim();

        if (query === '') {
            return this.creds();
        }

        return this.creds().filter((cred) => {
            return String(cred.scName || '').toLowerCase().indexOf(query.toLowerCase()) !== -1;
        });

    }

    onSelect(account) {
        let cred = this.creds().find((entry) => {
            return String(entry.scID) === account.id;
        });

        if (cred === undefined) {
            return;
        }

        // A credential the backend has flagged can't be reused as it stands —
        // send it back through the connect flow to be re-authorised instead.
        if (cred.sysActionFlag === 1) {
            this.props.newConnection(this.props.service, cred);
            return;
        }

        this.props.reuseInput({
            type: this.props.service,
            scID: cred.scID
        });
        this.props.closeAllModals();

    }

    toAccount(cred) {
        return {
            id: String(cred.scID),
            name: cred.scName,
            network: networkFromServiceCode(this.props.service),
            initials: initialsFromName(cred.scName),
            avatarUrl: cred.icon || undefined,
            inactive: cred.sysActionFlag === 1,
            subtype: (cred.sysActionFlag === 1) ? 'Needs reconnecting' : undefined
        };
    }

    render() {
        let serviceName = (this.props.service !== undefined)
                ? ConstantMixin.getInputServiceByCode(this.props.service)
                : '',
            matches = this.matches();

        return (
            <ConnectSocialDialog
                screen="accounts"
                assetBaseUrl={assetBase(this.props.app.config)}
                network={networkFromServiceCode(this.props.service) || undefined}
                title={(serviceName) ? `Reuse ${serviceName} Input` : 'Reuse Social Input'}
                subtitle="Pick a connected account to reuse, or connect a new one."
                accounts={matches.map((cred) => { return this.toAccount(cred); })}
                showSearch={this.creds().length > SEARCH_THRESHOLD}
                searchOpen={this.state.searchOpen}
                searchPlaceholder={(serviceName)
                    ? `Search connected ${serviceName} inputs`
                    : 'Search connected inputs'}
                query={this.state.query}
                emptyMessage={(this.state.query.trim() !== '')
                    ? 'No connected inputs match that search.'
                    : `You have no ${serviceName} inputs connected yet.`}
                showFooter={false}
                onSelectAccount={this.onSelect}
                onConnectNew={() => { this.props.newConnection(this.props.service); }}
                onSearchChange={(query) => { this.setState({query: query}); }}
                onSearchToggle={(open) => { this.setState({searchOpen: open}); }}
                onClose={this.props.closeAllModals} />
        );
    }
}

ReuseSocialInputModal.displayName = "ReuseSocialInputModal";

export default ReuseSocialInputModal;
